import { CURRENCIES, JOB_COST_RESPONSIBILITIES, JOB_PRICE_SOURCES, Money, RATE_BASES, type Currency } from '@chukua/common';
import { Form, Input, InputNumber, Radio, Select } from 'antd';
import { LOOSE_CARGO_RESPONSIBILITY_LABELS } from '../../cargo/policies/loose-cargo-responsibility-labels';
import { PRICE_SOURCE_LABELS, RATE_BASIS_LABELS } from '../../../../../shared/policies/rate-basis-labels';

const CURRENCY_OPTIONS = CURRENCIES.map((currency) => ({ value: currency, label: currency }));
const BASIS_OPTIONS = RATE_BASES.map((basis) => ({ value: basis, label: RATE_BASIS_LABELS[basis] }));
const PAYER_OPTIONS = JOB_COST_RESPONSIBILITIES.map((payer) => ({ value: payer, label: LOOSE_CARGO_RESPONSIBILITY_LABELS[payer] }));

interface CommercialTermsFieldsProps {
  customerPoNumberRequired: boolean;
  customerPaymentTermsDays: number;
}

/** Price, basis and source describe one quote (`createJobSchema` refuses them apart), so once any is filled the other two are required. */
function requiredWithPrice(label: string) {
  return [
    ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
      validator: (_: unknown, value: unknown) => {
        const priced = ['agreedPriceAmount', 'rateBasis', 'priceSource'].some((name) => getFieldValue(name) !== undefined && getFieldValue(name) !== null);
        return priced && (value === undefined || value === null) ? Promise.reject(new Error(`Enter the ${label} too`)) : Promise.resolve();
      },
    }),
  ];
}

/** The commercial-terms form's inputs (PRD → Job intake → Commercial terms; gitaumoses4/chukua#33). */
export function CommercialTermsFields({ customerPoNumberRequired, customerPaymentTermsDays }: CommercialTermsFieldsProps) {
  const currency = Form.useWatch<Currency | undefined>('agreedPriceCurrency') ?? 'KES';

  return (
    <>
      <div className="grid gap-x-4 md:grid-cols-3">
        <Form.Item
          name="agreedPriceAmount"
          label="Agreed price"
          dependencies={['rateBasis', 'priceSource', 'agreedPriceCurrency']}
          rules={requiredWithPrice('price')}
        >
          <InputNumber className="w-full" min={0} precision={Money.decimalPlaces(currency)} />
        </Form.Item>
        <Form.Item name="agreedPriceCurrency" label="Currency">
          <Select options={CURRENCY_OPTIONS} />
        </Form.Item>
        <Form.Item name="rateBasis" label="Per" dependencies={['agreedPriceAmount', 'priceSource']} rules={requiredWithPrice('basis')}>
          <Select allowClear options={BASIS_OPTIONS} />
        </Form.Item>
      </div>
      <Form.Item name="priceSource" label="Where the price came from" dependencies={['agreedPriceAmount', 'rateBasis']} rules={requiredWithPrice('source')}>
        <Radio.Group options={JOB_PRICE_SOURCES.map((source) => ({ value: source, label: PRICE_SOURCE_LABELS[source] }))} />
      </Form.Item>
      <div className="grid gap-x-4 md:grid-cols-2">
        <Form.Item name="tollsPayer" label="Who pays tolls">
          <Select allowClear options={PAYER_OPTIONS} />
        </Form.Item>
        <Form.Item name="weighbridgePayer" label="Who pays the weighbridge">
          <Select allowClear options={PAYER_OPTIONS} />
        </Form.Item>
        <Form.Item name="paymentTermsDaysOverride" label="Payment terms for this job (days)">
          <InputNumber className="w-full" min={1} max={365} precision={0} placeholder={`Customer's terms: ${customerPaymentTermsDays} days`} />
        </Form.Item>
        <Form.Item
          name="poNumber"
          label="PO number"
          extra={customerPoNumberRequired ? 'This customer requires a PO number before the job can be confirmed.' : undefined}
        >
          <Input maxLength={64} />
        </Form.Item>
      </div>
      <Form.Item name="cancellationTerms" label="Cancellation terms">
        <Input.TextArea rows={3} maxLength={1000} />
      </Form.Item>
    </>
  );
}
